/**
 * Kalorien aus einem Foto schätzen (27.10.2026, „Weg a“).
 *
 * Für Essen auswärts oder ohne bekannte Nährwerte: Ein KI-Modell — selbst
 * gehostet (Ollama) oder ein Dienst (Anthropic) — schaut auf das Foto und schätzt Gericht, Portion und Makros. **Das ist eine
 * Schätzung, kein geprüfter Wert** — sie wird deshalb dauerhaft markiert
 * (`food_log.estimate = 'photo'`) und nie als „Quelle: Verpackung“ verkauft.
 *
 * Hier stehen nur die Form des Ergebnisses und die Plausibilitätsregel —
 * Server (prüft die KI-Antwort) und App (prüft die Server-Antwort) benutzen
 * dieselbe.
 */

import { z } from 'zod';

/**
 * Wer schätzt (27.10.2026). `ollama` läuft auf dem eigenen Server — das Foto
 * verlässt ihn nicht; `anthropic` ist ein Dienst außerhalb. Davon hängt der
 * Hinweis ab, den die App vor dem Senden zeigt.
 */
export const PHOTO_ESTIMATE_PROVIDERS = ['anthropic', 'ollama'] as const;
export type PhotoEstimateProvider = (typeof PHOTO_ESTIMATE_PROVIDERS)[number];

/** Herkunft einer geschätzten Protokollzeile. Bisher nur das Foto. */
export const FOOD_LOG_ESTIMATES = ['photo'] as const;
export type FoodLogEstimate = (typeof FOOD_LOG_ESTIMATES)[number];

export const ESTIMATE_CONFIDENCES = ['low', 'medium', 'high'] as const;
export type EstimateConfidence = (typeof ESTIMATE_CONFIDENCES)[number];

/** Obergrenzen je Gericht — darüber ist die Antwort kaputt, nicht das Essen groß. */
export const PHOTO_ESTIMATE_LIMITS = {
  kcal: 5000,
  protein: 400,
  carbs: 700,
  fat: 400,
} as const;

/**
 * Passen Makros und Kalorien zusammen? Aus Protein, Kohlenhydraten und Fett
 * (4/4/9 kcal je g) kann nicht deutlich **mehr** Energie kommen, als die
 * Schätzung nennt. Weniger schon: Alkohol (7 kcal/g) und Ballaststoffe
 * stehen in keiner der drei Spalten.
 */
export function macrosFitKcal(value: {
  kcal: number;
  protein: number;
  carbs: number;
  fat: number;
}): boolean {
  const fromMacros = value.protein * 4 + value.carbs * 4 + value.fat * 9;
  return fromMacros <= value.kcal * 1.35 + 60;
}

const amount = (max: number) => z.number().finite().min(0).max(max);

/** Ergebnis einer Schätzung, wie Server und App es weiterreichen. */
export const PhotoEstimateSchema = z
  .object({
    dish: z.string().trim().min(1).max(160),
    portion: z.string().trim().max(160),
    kcal: amount(PHOTO_ESTIMATE_LIMITS.kcal),
    protein: amount(PHOTO_ESTIMATE_LIMITS.protein),
    carbs: amount(PHOTO_ESTIMATE_LIMITS.carbs),
    fat: amount(PHOTO_ESTIMATE_LIMITS.fat),
    confidence: z.enum(ESTIMATE_CONFIDENCES),
  })
  .refine(macrosFitKcal, { message: 'Makros passen nicht zu den Kalorien' });

export type PhotoEstimate = z.infer<typeof PhotoEstimateSchema>;

/** Gründe, die der Server bei einer gescheiterten Schätzung nennt. */
export const PHOTO_ESTIMATE_REASONS = [
  /** Kein Anbieter eingerichtet — die Funktion gibt es auf diesem Server nicht. */
  'disabled',
  /** Kinderkonto (E16): keine Körperzahlen, kein Foto an Dritte. */
  'child',
  /** Auf dem Foto ist kein Essen zu erkennen. */
  'not_food',
  /** Die KI hat abgelehnt. */
  'refused',
  /** Die KI-Antwort ist unvollständig oder unplausibel. */
  'invalid',
  /** Der KI-Dienst ist nicht erreichbar, überlastet oder zu langsam. */
  'upstream',
  /** Zu viele Schätzungen in kurzer Zeit. */
  'rate',
  /**
   * Im Hintergrund (28.10.2026): Der Server wurde neu gestartet, bevor das
   * Foto dran war — es lag nur im Speicher. Nie still: das Gerät kann es
   * erneut senden.
   */
  'aborted',
  /** Die Warteschlange ist voll — später erneut. */
  'busy',
  /**
   * Das Konto ist verschlüsselt, hat aber noch kein Schlüsselpaar — ohne das
   * könnte der Server das Ergebnis nur als Klartext ablegen. Das Gerät legt
   * es an und sendet noch einmal.
   */
  'identity',
] as const;
export type PhotoEstimateReason = (typeof PHOTO_ESTIMATE_REASONS)[number];
